import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Plus, Boxes } from 'lucide-react';
import ShimmerImage from '../../../components/ShimmerImage';
import { Skeleton, SkeletonCardGrid } from '../../../components/Skeleton';
import {
  hardwareApi, money, STATUS_CHIP,
  type DeviceBundleRow, type DeviceModelRow,
} from '../../../services/hardwareApi';
import BundleBuilder from './BundleBuilder';

/**
 * Device bundles — named kits. This is what a customer sees on a deal, so the
 * name, description and hero image matter as much as the parts list.
 */
const BundlesTab: React.FC = () => {
  const [bundles, setBundles] = useState<DeviceBundleRow[]>([]);
  const [models, setModels] = useState<DeviceModelRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [b, m] = await Promise.all([hardwareApi.listBundles(), hardwareApi.listModels()]);
      setBundles(b.items || []);
      setModels(m.items || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load bundles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const nameOf = (id: string) => models.find((m) => m.modelId === id)?.modelName || id;

  if (loading) {
    // Skeleton mirrors the bundle card grid so the page shape paints instantly
    return (
      <div>
        <div className="mb-5 flex items-center justify-between gap-3">
          <Skeleton variant="light" className="h-4 w-72 max-w-full" />
          <Skeleton variant="light" className="h-9 w-28 rounded-lg shrink-0" />
        </div>
        <SkeletonCardGrid count={4} />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      <div className="mb-5 flex items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          A bundle is a named kit — pick compatible devices, and it becomes selectable on a deal.
        </p>
        <button
          onClick={() => setEditing('')}
          className="flex shrink-0 items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold text-white"
          style={{ background: '#DC143C' }}
        >
          <Plus size={14} /> New bundle
        </button>
      </div>

      {bundles.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
          <Boxes size={28} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm font-medium text-gray-700">No bundles yet</p>
          <p className="mt-1 text-xs text-gray-500">
            Build a kit from catalog devices, then attach it to a deal.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {bundles.map((b) => {
            const problems = (b.missing?.length || 0) + (b.incompatible?.length || 0);
            return (
              <button
                key={b.bundleId}
                onClick={() => setEditing(b.bundleId)}
                className="rounded-xl border border-gray-200 bg-white p-4 text-left transition hover:border-gray-300 hover:shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <ShimmerImage
                    src={b.heroImageUrl}
                    alt={b.name}
                    width={64}
                    height={64}
                    objectFit="contain"
                    className="shrink-0 rounded-lg"
                    fallbackBgColor="bg-gray-900"
                    fallbackIcon={<Boxes size={24} className="text-gray-500" />}
                    variant="dark"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-900">{b.name}</span>
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_CHIP[b.status] || 'bg-gray-100'}`}>
                        {b.status.toUpperCase()}
                      </span>
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-[11px] text-gray-600">{b.description}</p>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {b.components.map((c) => (
                        <span key={c.modelId} className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] text-gray-600">
                          {nameOf(c.modelId)}{c.quantity > 1 ? ` ×${c.quantity}` : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
                  <span className="text-[11px] text-gray-500">
                    components {money(b.componentsLandedCents)} + outbound {money(b.combinedOutboundShippingCents)}
                  </span>
                  <span className="font-mono text-sm font-bold text-gray-900">{money(b.totalCostCents)}</span>
                </div>

                {problems > 0 && (
                  <div className="mt-2 flex items-center gap-1.5 rounded-lg bg-red-50 px-2 py-1.5 text-[11px] font-medium text-red-700">
                    <AlertTriangle size={12} />
                    {b.missing?.length ? `${b.missing.length} unknown device(s)` : ''}
                    {b.missing?.length && b.incompatible?.length ? ' · ' : ''}
                    {b.incompatible?.length ? `${b.incompatible.length} incompatible` : ''}
                  </div>
                )}
                {problems === 0 && b.unmetRequires?.length > 0 && (
                  <div className="mt-2 flex items-center gap-1.5 rounded-lg bg-amber-50 px-2 py-1.5 text-[11px] font-medium text-amber-800">
                    <AlertTriangle size={12} />
                    missing recommended: {b.unmetRequires.map(nameOf).join(', ')}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {editing !== null && (
        <BundleBuilder
          bundleId={editing || null}
          models={models}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

export default BundlesTab;
